import React, { useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Webcam from 'react-webcam';

const DocUpload = (props) => {
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const webcamRef = useRef(null);

  const validationSchema = Yup.object().shape({
    aadharFront: Yup.mixed().required('Aadhar front image is required'),
    aadharBack: Yup.mixed().required('Aadhar back image is required'),
    selfie: Yup.mixed().required('Selfie is required'),
  });

  const handleCaptureSelfie = (setFieldValue) => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    // Don't set field value yet, wait for confirmation
  };

  const handleConfirmSelfie = (setFieldValue) => {
    setFieldValue('selfie', capturedImage);
    // setShowWebcam(false);
    // setCapturedImage(null);
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleFileChange = (event, setFieldValue, field) => {
    const file = event.currentTarget.files[0];
    setFieldValue(field, file);
  };

  const toggleCamera = () => {
    setFacingMode(prevMode => 
      prevMode === "user" ? "environment" : "user"
    );
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (values) => {
    try {
      // Convert Aadhar front image
      const aadharFrontBase64 = values.aadharFront instanceof File 
        ? await convertFileToBase64(values.aadharFront)
        : values.aadharFront;

      // Convert Aadhar back image
      const aadharBackBase64 = values.aadharBack instanceof File 
        ? await convertFileToBase64(values.aadharBack)
        : values.aadharBack;

      // Convert selfie to base64 if it's a File object
      const selfieBase64 = values.selfie instanceof File
        ? await convertFileToBase64(values.selfie)
        : values.selfie;

      // Call the prop function with converted images
      props?.redirectToStep({
        aadhaarfrontimg: aadharFrontBase64,
        aadhaarbackimg: aadharBackBase64,
        selfieimg: selfieBase64
      });
    } catch (error) {
      console.error('Error converting images:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Document Upload / ಡಾಕ್ಯುಮೆಂಟ್‌ಗಳ ಅಪ್‌ಲೋಡ್</h2>
      
      <Formik
        initialValues={{
          aadharFront: null,
          aadharBack: null,
          selfie: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form className="space-y-6">
            {/* Aadhar Front */}
            <div>
              <label className="block mb-2">Aadhar Card Front / ಆಧಾರ್ ಮುಂಭಾಗದ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, setFieldValue, 'aadharFront')}
                className="w-full p-2 border rounded"
              />
              {errors.aadharFront && touched.aadharFront && (
                <div className="text-red-500 text-sm mt-1">{errors.aadharFront}</div>
              )}
            </div>

            {/* Aadhar Back */}
            <div>
              <label className="block mb-2">Aadhar Card Back / ಆಧಾರ್ ಹಿಂದಿನ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, setFieldValue, 'aadharBack')}
                className="w-full p-2 border rounded"
              />
              {errors.aadharBack && touched.aadharBack && (
                <div className="text-red-500 text-sm mt-1">{errors.aadharBack}</div>
              )}
            </div>

            {/* Modified Selfie Section */}
            <div>
              <label className="block mb-2">Selfie / ಸೆಲ್ಫಿ ಫೋಟೋ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ</label>
              {!showWebcam ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setFieldValue, 'selfie')}
                    className="w-full p-2 border rounded"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebcam(true)}
                    className="bg-[#5A786F] text-white px-4 py-2 rounded w-full"
                  >
                    Take Selfie with Webcam
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {capturedImage ? (
                    <div className="space-y-4">
                      <img 
                        src={capturedImage} 
                        alt="Captured selfie" 
                        className="w-64 h-64 object-cover mx-auto rounded"
                      />
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleConfirmSelfie(setFieldValue)}
                          className="bg-green-500 text-white px-6 py-2 rounded"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={handleRetake}
                          className="bg-yellow-500 text-white px-6 py-2 rounded"
                        >
                          Retake
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowWebcam(false);
                            setCapturedImage(null);
                          }}
                          className="bg-red-500 text-white px-6 py-2 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <Webcam
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          className="w-full max-w-md mx-auto rounded"
                          videoConstraints={{
                            facingMode: facingMode,
                            width: 1280,
                            height: 720
                          }}
                        />
                        <button
                          type="button"
                          onClick={toggleCamera}
                          className="absolute top-2 right-2 bg-gray-800 text-white p-2 rounded-full opacity-70 hover:opacity-100"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-6 w-6" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleCaptureSelfie(setFieldValue)}
                          className="bg-green-500 text-white px-6 py-2 rounded"
                        >
                          Capture
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowWebcam(false)}
                          className="bg-red-500 text-white px-6 py-2 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {errors.selfie && touched.selfie && (
                <div className="text-red-500 text-sm mt-1">{errors.selfie}</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-[#5A786F] text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              Submit Documents
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default DocUpload;
